import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { appApiEndpoint } from "@/environment";
import { io } from "socket.io-client";
import log from "loglevel";
import { useSelector } from "react-redux";

// Create a context
const SocketContext = createContext(null);

// Custom hook to use the socket context
// eslint-disable-next-line react-refresh/only-export-components
export const useSocket = () => {
  return useContext(SocketContext);
};

// Provider component
const SocketProvider = ({ user, children }) => {
  const socketRef = useRef(null); // 👈 useRef instead of state
  const [eventLog, setEventLog] = useState([]); // Store all events
  const [isConnected, setIsConnected] = useState(false);

  const onConnect = () => {
    log.debug("Socket.io connected!");
    setIsConnected(true);
  };

  useEffect(() => {
    const cleanupSocket = () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setEventLog([]);
      setIsConnected(false);
    };

    log.debug("User is: ", user);

    if (!user || user.type !== 2) {
      log.debug(
        "User is missing or admin about to impersonate — closing socket if any"
      );
      cleanupSocket();
      return;
    }

    log.debug("Creating socket.io connection");

    // Create socket.io connection
    const socketUrl = `${appApiEndpoint}`;
    log.debug("Socket URL:", socketUrl);
    const socketInstance = io(socketUrl, {
      path: "/socket.io",
      withCredentials: true,
      query: {
        employerId: user.employerId,
      },
      reconnectionDelay: 2000, // Start with a 2s delay
      reconnectionDelayMax: 60000, // Max delay of 60s
    });
    socketRef.current = socketInstance;

    socketInstance.onAny((eventName, ...args) => {
      log.debug(`Received event: ${eventName}`, args);
      // Store the event in the log but limit the log length to 10
      setEventLog((prevLog) => {
        const newLog = [...prevLog, { eventName, args }];
        return newLog.slice(Math.max(newLog.length - 10, 0));
      });
    });
    socketInstance.on("connect", onConnect);

    return cleanupSocket;
  }, [user]);

  const clearEventLog = () => {
    setEventLog([]);
  };

  log.debug("The eventLog is: ", eventLog);

  return (
    <SocketContext.Provider
      value={{
        socket: socketRef.current,
        eventLog,
        clearEventLog,
        isConnected,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export const SocketProviderWrapper = ({ children }) => {
  const user = useSelector((state) => state.auth.user);

  if (user === undefined) {
    log.warn("User not yet defined");
    return null;
  }

  return <SocketProvider user={user}>{children}</SocketProvider>;
};
